import { buildFollowUp, type Rfq, type Send, t } from '@procumi/shared';
import { useState } from 'react';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { copyText } from '../lib/clipboard';
import { autoSendAllowed, currentUser } from '../lib/me';
import { Dialog } from './Dialog';

/**
 * Follow-up-ul. În Echipă și Companie îl trimite Procumi, în numele omului, cu
 * răspunsul la adresa lui. În Start pleacă din programul de mail al omului:
 * aici se pregătește, iar „Am trimis” doar notează că a plecat — de atunci se
 * numără din nou zilele de tăcere.
 */
export function FollowUpDialog({
  rfq,
  send,
  signature,
  toast,
  onSent,
  onClose,
}: {
  rfq: Rfq;
  send: Send;
  signature: string;
  toast: Toast;
  onSent: (updated: Rfq) => void;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const auto = autoSendAllowed();

  const mailIt = async () => {
    setBusy(true);
    try {
      const updated = await api.mailFollowUp(rfq.id, send.id);
      onSent(updated);
      onClose();
      toast(t('Follow-up trimis la {0}', send.supplier.name));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const text = t(
    buildFollowUp(rfq, send, { name: send.supplier.name, contact: send.contact.name }, signature),
  );

  const subject = t('Re: Cerere de ofertă – {0}', rfq.title);
  // Deschide mailul gata scris în programul de mail; lungimea e mică, deci încape în link.
  const mailto = `mailto:${send.contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;

  const markSent = async () => {
    setBusy(true);
    try {
      const updated = await api.followUp(rfq.id, send.id);
      onSent(updated);
      onClose();
      toast(t('Notat: follow-up către {0}', send.supplier.name));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose}>
      <div className="dlg-h">
        <div>
          <h2>{t('Follow-up către {0}', send.supplier.name)}</h2>
          <p className="hint">
            {send.contact.name ? `${send.contact.name} · ` : ''}
            {send.contact.email}
            {send.followUps ? t(' · deja ai trimis {0} follow-up', send.followUps) : ''}
          </p>
        </div>
      </div>
      <div className="dlg-b">
        <p className="hint" style={{ margin: 0 }}>
          {auto
            ? t(
                'Procumi îl trimite în numele tău; răspunsul vine la {0}. Dacă preferi, îl copiezi și îl trimiți tu, apoi apeși „Am trimis”.',
                currentUser().email,
              )
            : t(
                'Îl trimiți tu, din programul tău de mail, ca răspunsul să vină la tine. După ce pleacă, apasă „Am trimis”, ca să știm de când aștepți.',
              )}
        </p>
        <div className="mail">{text}</div>
      </div>
      <div className="dlg-f">
        <button className="btn" onClick={onClose}>
          {t('Anulează')}
        </button>
        <button className="btn" onClick={() => void copyText(text, toast)}>
          {t('Copiază textul')}
        </button>
        {send.contact.email ? (
          <a className="btn" href={mailto}>
            {t('Deschide în programul de mail')}
          </a>
        ) : null}
        <button className={auto ? 'btn' : 'btn primary'} disabled={busy} onClick={() => void markSent()}>
          {t('Am trimis')}
        </button>
        {auto ? (
          <button
            className="btn primary"
            disabled={busy || !send.contact.email}
            onClick={() => void mailIt()}
          >
            {busy ? t('Se trimite…') : t('Trimite din Procumi')}
          </button>
        ) : null}
      </div>
    </Dialog>
  );
}
